import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const button = cva(
  'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030712] disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        gold:
          'bg-gradient-to-br from-[#FFF066] via-[#FFD700] to-[#FFA500] text-black hover:brightness-110 focus-visible:ring-gold',
        primary:
          'bg-white text-black hover:bg-gray-100 focus-visible:ring-white/50',
        secondary:
          'bg-gray-900 text-white border border-gray-800 hover:border-gold/60 hover:bg-gray-800 focus-visible:ring-gold/40',
        ghost:
          'text-gray-300 hover:bg-gray-900 hover:text-white focus-visible:ring-gold/40',
        danger:
          'bg-red-600 text-white hover:bg-red-500 focus-visible:ring-red-400',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        xl: 'h-14 px-8 text-lg',
      },
    },
    defaultVariants: { variant: 'gold', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(button({ variant, size }), className)} {...props} />
  ),
);
Button.displayName = 'Button';
